import type { Creation, Kind } from "../src/domain/types";
import type { Idea, SelectedThing } from "../src/providers/types";
import { api, ApiError, type Who } from "./api";
import { describeCreation, titleCase } from "./describe";
import { h } from "./dom";
import { buildZips, download, type Downloads } from "./export";
import { Library, mergeLibraries, type Entry } from "./library";
import { ranksView } from "./ranks-view";
import { toIconDataUrl } from "./texture";

const KIND_INFO: Record<Kind, { emoji: string; label: string; placeholder: string }> = {
  weapon: { emoji: "⚔️", label: "Weapon", placeholder: "a sword made of lava that sets mobs on fire and makes me run fast" },
  tool: { emoji: "⛏️", label: "Tool", placeholder: "a super pickaxe that gives me haste and night vision when I right-click" },
  item: { emoji: "✨", label: "Magic", placeholder: "a magic wand that calls lightning wherever I point" },
  food: { emoji: "🍪", label: "Food", placeholder: "a rainbow cookie that makes me jump super high" },
  armor: { emoji: "🛡️", label: "Armor", placeholder: "rocket boots that make me really fast" },
  mob: { emoji: "🧟", label: "Mob", placeholder: "a giant glowing zombie king holding a diamond sword" },
};
const KINDS = Object.keys(KIND_INFO) as Kind[];

interface Current extends Entry {
  libraryIndex: number | null;
}

interface State {
  tab: "make" | "ranks";
  kind: Kind;
  prompt: string;
  ideas: Idea[];
  ideasFor: string | null;
  current: Current | null;
  drawing: boolean;
  busy: string | null;
  status: { text: string; error: boolean };
}

let library = new Library(window.localStorage, "guest");
let librarySynced = false;
let syncing: Promise<void> | null = null;
let username = "";
let parent = false;
const root = document.getElementById("app")!;
const state: State = {
  tab: "make",
  kind: "weapon",
  prompt: "",
  ideas: [],
  ideasFor: null,
  current: null,
  drawing: false,
  busy: null,
  status: { text: "", error: false },
};

function update(patch: Partial<State>) {
  Object.assign(state, patch);
  render();
}

function say(text: string, error = false) {
  update({ status: { text, error } });
}

async function run(label: string, task: () => Promise<void>) {
  update({ busy: label, status: { text: "", error: false } });
  try {
    await task();
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) return showLogin();
    say(err instanceof Error ? err.message : "Something went wrong.", true);
  } finally {
    update({ busy: null });
  }
}

async function createThing() {
  const prompt = state.prompt.trim();
  if (!prompt) return say("Type your idea first!", true);
  await run("Crafting", async () => {
    const { creation } = await api.create(prompt, state.kind);
    update({ current: { creation, icon: null, libraryIndex: null }, ideas: [] });
    say(`${creation.name} is ready! Painting its picture...`);
    void drawIcon();
  });
}

async function drawIcon() {
  const current = state.current;
  if (!current) return;
  update({ drawing: true });
  try {
    const { png } = await api.texture(current.creation.texturePrompt, current.creation.kind);
    const icon = await toIconDataUrl(png);
    if (state.current?.creation === current.creation) {
      update({ current: { ...state.current, icon } });
      say(`Done! Save ${current.creation.name} to My Mods to keep it.`);
    }
  } catch (err) {
    say(err instanceof Error ? `${err.message} (You can still save it - it will look like a normal ${titleCase(current.creation.baseItem)}.)` : "Couldn't paint it.", true);
  } finally {
    update({ drawing: false });
  }
}

async function getIdeas(kind: Kind, selected: SelectedThing | null) {
  await run("Thinking", async () => {
    const { ideas } = await api.ideas(kind, selected, selected ? "" : state.prompt.trim());
    update({ ideas, kind, ideasFor: selected?.name ?? null });
    document.getElementById("ideas")?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  });
}

function saveCurrent() {
  const current = state.current;
  if (!current) return;
  try {
    if (current.libraryIndex === null) library.save(current.creation, current.icon);
    else library.replace(current.libraryIndex, current.creation, current.icon);
    pushLibrary();
  } catch {
    return say("My Mods is full! Delete something you don't need, then save again.", true);
  }
  update({ current: null });
  say(`Saved! ${current.creation.name} is in My Mods.`);
}

function packUp(): Downloads | null {
  const entries = library.all();
  if (!entries.length) {
    say("Save something to My Mods first!", true);
    return null;
  }
  try {
    return buildZips(entries);
  } catch {
    say("Couldn't pack up your mods. Try deleting the newest one and download again.", true);
    return null;
  }
}

function downloadJar() {
  const packs = packUp();
  if (!packs) return;
  download("MyMods.jar", packs.fabricJar, "application/java-archive");
  say("Downloaded MyMods.jar! Put it in your mods folder (steps below).");
}

function downloadPacks() {
  const packs = packUp();
  if (!packs) return;
  download("MyMods-DataPack.zip", packs.datapack);
  setTimeout(() => download("MyMods-ResourcePack.zip", packs.resourcepack), 400);
  say("Downloaded both zips! Follow the server steps below.");
}

function render() {
  const view = state.tab === "ranks"
    ? ranksView(parent, render)
    : [createPanel(), ...(state.current ? [previewPanel(state.current)] : []), libraryPanel(), guidePanel()];
  root.replaceChildren(accountBar(), ...(parent ? [activityPanel()] : []), ...view);
}

function createPanel() {
  const info = KIND_INFO[state.kind];
  const busy = state.busy !== null;
  return h("section", { class: "panel", "aria-labelledby": "create-title" },
    h("h2", { id: "create-title" }, "1. What do you want to make?"),
    h("div", { class: "hotbar", role: "group", "aria-label": "Type of thing" },
      ...KINDS.map((kind) => h("button", {
        type: "button",
        "aria-pressed": String(kind === state.kind),
        onclick: () => update({ kind, ideas: [], ideasFor: null }),
      }, h("span", { class: "emoji", "aria-hidden": "true" }, KIND_INFO[kind].emoji), KIND_INFO[kind].label)),
    ),
    h("label", { class: "label", for: "prompt" }, `Describe your ${info.label.toLowerCase()}:`),
    textarea(info.placeholder),
    h("div", { class: "btn-row" },
      h("button", { class: "btn btn-go", type: "button", disabled: busy, onclick: () => void createThing() },
        state.busy === "Crafting" ? "Crafting..." : "⚒ Create it!"),
      h("button", { class: "btn btn-idea", type: "button", disabled: busy, onclick: () => void getIdeas(state.kind, null) },
        state.busy === "Thinking" ? "Thinking..." : `💡 Give me ${info.label.toLowerCase()} ideas`),
    ),
    h("p", { class: `status${state.status.error ? " error" : ""}`, role: "status" }, state.status.text),
    state.ideas.length ? ideasList() : null,
  );
}

function textarea(placeholder: string) {
  const el = h("textarea", { id: "prompt", maxlength: 500, placeholder: `Example: ${placeholder}` }) as HTMLTextAreaElement;
  el.value = state.prompt;
  el.addEventListener("input", () => { state.prompt = el.value; });
  el.addEventListener("keydown", (e) => {
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) void createThing();
  });
  return el;
}

function ideasList() {
  const heading = state.ideasFor ? `Ideas to upgrade ${state.ideasFor}:` : "Ideas! Click one to use it:";
  return h("div", { id: "ideas" },
    h("p", { class: "label" }, heading),
    h("div", { class: "ideas" },
      ...state.ideas.map((idea, i) => h("button", {
        class: "idea", type: "button", style: `animation-delay:${i * 70}ms`,
        onclick: () => {
          update({ prompt: idea.prompt, ideas: [] });
          const box = document.getElementById("prompt");
          box?.focus();
          box?.scrollIntoView({ behavior: "smooth", block: "center" });
        },
      }, h("strong", {}, idea.name), h("span", {}, idea.pitch))),
    ),
  );
}

function previewPanel(current: Current) {
  const c = current.creation;
  const busy = state.busy !== null;
  return h("section", { class: "panel", "aria-labelledby": "preview-title" },
    h("h2", { id: "preview-title" }, "2. Check it out!"),
    h("div", { class: "preview" },
      h("div", { class: `icon-frame slot${state.drawing ? " loading" : ""}` },
        current.icon ? h("img", { src: current.icon, alt: `Picture of ${c.name}` }) : null),
      h("div", {},
        tooltip(c),
        c.recipe ? crafting(c, current.icon) : h("p", {}, `Get it with: /function mymods:${c.mob ? "summon" : "give"}/${c.id}`),
        h("div", { class: "btn-row" },
          h("button", { class: "btn btn-go", type: "button", disabled: busy, onclick: saveCurrent },
            current.libraryIndex === null ? "💾 Save to My Mods" : "💾 Save changes"),
          h("button", { class: "btn", type: "button", disabled: busy || state.drawing, onclick: () => void drawIcon() },
            state.drawing ? "Painting..." : "🎨 Redraw picture"),
          h("button", { class: "btn btn-idea", type: "button", disabled: busy, onclick: () => void getIdeas(c.kind, c) }, "💡 Upgrade ideas"),
          h("button", { class: "btn btn-danger", type: "button", disabled: busy, onclick: () => update({ current: null }) }, "✖ Toss it"),
        ),
      ),
    ),
  );
}

function tooltip(c: Creation) {
  return h("div", { class: "tooltip" },
    h("p", { class: `name rarity-${c.rarity}` }, c.name),
    c.description ? h("p", { class: "lore" }, c.description) : null,
    h("ul", {}, ...describeCreation(c).map((line) => h("li", { class: line.style }, line.text))),
  );
}

function crafting(c: Creation, icon: string | null) {
  const recipe = c.recipe!;
  const width = recipe.pattern[0].length;
  const cells = Array.from({ length: 9 }, (_, i) => {
    const row = recipe.pattern[Math.floor(i / 3)] ?? "";
    const col = i % 3;
    const symbol = col < width ? row[col] : " ";
    const item = symbol && symbol !== " " ? recipe.key[symbol] : null;
    return h("div", { class: "slot", title: item ? titleCase(item) : "empty" }, item ? titleCase(item) : "");
  });
  return h("div", { class: "crafting", "aria-label": "Crafting recipe" },
    h("div", { class: "grid3" }, ...cells),
    h("span", { class: "arrow", "aria-hidden": "true" }, "➜"),
    h("div", { class: "result-slot slot" }, icon ? h("img", { src: icon, alt: c.name }) : c.name),
  );
}

function libraryPanel() {
  const entries = library.all();
  return h("section", { class: "panel", "aria-labelledby": "library-title" },
    h("h2", { id: "library-title" }, `3. My Mods (${entries.length})`),
    entries.length
      ? h("div", { class: "library-grid" }, ...entries.map((entry, i) => libraryCard(entry, i)))
      : h("p", { class: "empty" }, "Nothing here yet. Make something awesome!"),
    h("div", { class: "btn-row" },
      h("button", { class: "btn btn-go", type: "button", disabled: !entries.length, onclick: downloadJar }, "⬇ Download for my mods folder"),
      h("button", { class: "btn btn-small", type: "button", disabled: !entries.length, onclick: downloadPacks }, "Data pack + resource pack (for servers)"),
    ),
  );
}

function libraryCard(entry: Entry, index: number) {
  const c = entry.creation;
  return h("div", { class: "card slot", style: `animation-delay:${index * 50}ms` },
    entry.icon ? h("img", { src: entry.icon, alt: "" }) : h("span", { class: "placeholder", "aria-hidden": "true" }, KIND_INFO[c.kind].emoji),
    h("span", { class: `card-name rarity-${c.rarity}` }, c.name),
    h("span", { class: "card-kind" }, KIND_INFO[c.kind].label),
    h("div", { class: "btn-row" },
      h("button", { class: "btn btn-small", type: "button", onclick: () => openEntry(entry, index) }, "Open"),
      h("button", { class: "btn btn-small btn-idea", type: "button", disabled: state.busy !== null, onclick: () => void getIdeas(c.kind, c) }, "💡"),
      h("button", {
        class: "btn btn-small btn-danger", type: "button", "aria-label": `Delete ${c.name}`,
        onclick: () => { if (window.confirm(`Delete ${c.name}?`)) { library.remove(index); pushLibrary(); render(); } },
      }, "🗑"),
    ),
  );
}

function openEntry(entry: Entry, index: number) {
  update({ current: { ...entry, libraryIndex: index } });
  document.getElementById("preview-title")?.scrollIntoView({ behavior: "smooth" });
}

function guidePanel() {
  return h("section", { class: "panel guide", "aria-labelledby": "guide-title" },
    h("h2", { id: "guide-title" }, "4. Put it in Minecraft (Java Edition with Fabric)"),
    h("ol", {},
      h("li", {}, "Click ", h("strong", {}, "Download for my mods folder"), ". You get ", h("code", {}, "MyMods.jar"), "."),
      h("li", {}, "Put ", h("code", {}, "MyMods.jar"), " in your ", h("code", {}, "mods"),
        " folder (the same place as your other mods). If there's an old MyMods.jar in there, delete the old one first!"),
      h("li", {}, "You need Fabric and Fabric API, just like your other mods. Start Minecraft with the Fabric profile."),
      h("li", {}, "Open any world. Click the gold message, or type ", h("code", {}, "/trigger mymods_menu"), ", then click ", h("strong", {}, "[Get]"), ". No cheats needed!"),
      h("li", {}, "Right-click items with powers to use them. Hit mobs to see on-hit powers. Your recipes show up in the recipe book."),
    ),
    h("details", {},
      h("summary", {}, "Playing on the family server, or no Fabric?"),
      h("p", {}, "Click \"Data pack + resource pack\". A grown-up puts MyMods-DataPack.zip in the server's world/datapacks folder and runs /reload. " +
        "Put MyMods-ResourcePack.zip in your resourcepacks folder (Options → Resource Packs → Open Pack Folder) and turn it on so you can see the pictures."),
    ),
    h("details", {},
      h("summary", {}, "Made new stuff?"),
      h("p", {}, "Download again and swap the old MyMods.jar for the new one. Everything in My Mods is in the new file."),
    ),
  );
}

function activityPanel() {
  const body = h("tbody", {});
  const status = h("p", { class: "status" }, "Loading...");
  const load = async () => {
    status.textContent = "Loading...";
    try {
      const { entries } = await api.activity();
      body.replaceChildren(...entries.map((e) => h("tr", {},
        h("td", {}, new Date(e.at).toLocaleString()),
        h("td", {}, e.user),
        h("td", {}, `${e.action} (${e.kind})`),
        h("td", {}, e.input || "-"),
        h("td", { class: e.error ? "warn" : "" }, e.error ?? e.result ?? ""),
      )));
      status.textContent = entries.length ? "" : "No activity yet.";
    } catch (err) {
      status.textContent = err instanceof Error ? err.message : "Couldn't load activity.";
    }
  };
  void load();
  return h("section", { class: "panel activity", "aria-labelledby": "activity-title" },
    h("h2", { id: "activity-title" }, "Parent view: what everyone asked for"),
    h("div", { class: "table-wrap" }, h("table", {},
      h("thead", {}, h("tr", {}, ...["When", "Who", "What", "They typed", "Result"].map((t) => h("th", { scope: "col" }, t)))),
      body,
    )),
    status,
    h("div", { class: "btn-row" },
      h("button", { class: "btn btn-small", type: "button", onclick: () => void load() }, "Refresh"),
      h("button", { class: "btn btn-small btn-go", type: "button", onclick: () => void downloadFamilyPack() }, "⬇ Family server pack (everyone's mods)"),
    ),
  );
}

async function downloadFamilyPack() {
  try {
    const { libraries } = await api.allLibraries();
    const entries = Object.values(libraries).flat();
    if (!entries.length) return say("Nobody has saved any mods yet.", true);
    const packs = buildZips(entries);
    download("Family-MyMods-DataPack.zip", packs.datapack);
    setTimeout(() => download("Family-MyMods-ResourcePack.zip", packs.resourcepack), 400);
    say(`Downloaded the family pack with ${entries.length} creations from ${Object.keys(libraries).join(", ")}.`);
  } catch (err) {
    say(err instanceof Error ? err.message : "Couldn't build the family pack.", true);
  }
}

function accountBar() {
  const tab = (id: State["tab"], label: string) => h("button", {
    class: "btn btn-small tab", type: "button", "aria-pressed": String(state.tab === id), onclick: () => update({ tab: id }),
  }, label);
  return h("div", { class: "account" },
    h("nav", { class: "tabs", "aria-label": "Sections" }, tab("make", "⚒ Make stuff"), tab("ranks", "👑 Ranks")),
    h("span", {}, "Crafting as ", h("strong", {}, username)),
    h("button", { class: "btn btn-small", type: "button", onclick: () => void logout() }, "Log out"),
  );
}

function signIn(who: Who) {
  username = who.username;
  parent = who.parent;
  const name = who.username;
  library = new Library(window.localStorage, name);
  librarySynced = false;
  update({ current: null, ideas: [], status: { text: `Welcome, ${name}!`, error: false } });
  void syncLibrary();
}

function syncLibrary(): Promise<void> {
  syncing ??= runSync().finally(() => { syncing = null; });
  return syncing;
}

async function runSync() {
  try {
    const { entries } = await api.library();
    library.replaceAll(mergeLibraries(library.all(), entries));
    await api.saveLibrary(library.all());
    librarySynced = true;
    render();
  } catch {
    say("Couldn't sync My Mods with the website. Your stuff is still saved on this computer.", true);
  }
}

function pushLibrary() {
  if (!librarySynced) return void syncLibrary();
  void api.saveLibrary(library.all()).catch(() => say("Couldn't back up My Mods to the website. Try again later.", true));
}

async function logout() {
  await api.logout().catch(() => undefined);
  showLogin();
}

function showLogin() {
  const user = h("input", { type: "text", id: "username", autocomplete: "username", placeholder: "Minecraft username", maxlength: 32 }) as HTMLInputElement;
  const pass = h("input", { type: "password", id: "password", autocomplete: "current-password", placeholder: "Password", maxlength: 128 }) as HTMLInputElement;
  const status = h("p", { class: "status error", role: "status" });
  const submit = async (event: Event) => {
    event.preventDefault();
    try {
      signIn(await api.login(user.value, pass.value));
    } catch (err) {
      status.textContent = err instanceof Error ? err.message : "Try again";
      pass.select();
    }
  };
  root.replaceChildren(h("form", { class: "panel login", onsubmit: submit },
    h("h2", {}, "Who goes there?"),
    h("label", { class: "label", for: "username" }, "Username"),
    user,
    h("label", { class: "label", for: "password" }, "Password"),
    pass,
    h("div", { class: "btn-row centered" }, h("button", { class: "btn btn-go", type: "submit" }, "Let me in!")),
    status,
  ));
  user.focus();
}

function showSetPassword(token: string) {
  const pass = h("input", { type: "password", id: "new-password", autocomplete: "new-password", placeholder: "New password", maxlength: 128 }) as HTMLInputElement;
  const again = h("input", { type: "password", id: "again", autocomplete: "new-password", placeholder: "Type it again", maxlength: 128 }) as HTMLInputElement;
  const status = h("p", { class: "status error", role: "status" });
  const submit = async (event: Event) => {
    event.preventDefault();
    if (pass.value !== again.value) {
      status.textContent = "Those don't match. Try again!";
      return;
    }
    try {
      signIn(await api.setPassword(token, pass.value));
    } catch (err) {
      status.textContent = err instanceof Error ? err.message : "Try again";
    }
  };
  root.replaceChildren(h("form", { class: "panel login", onsubmit: submit },
    h("h2", {}, "Make your password"),
    h("p", {}, "At least 8 letters or numbers. Don't share it!"),
    h("label", { class: "label", for: "new-password" }, "New password"),
    pass,
    h("label", { class: "label", for: "again" }, "Type it again"),
    again,
    h("div", { class: "btn-row centered" }, h("button", { class: "btn btn-go", type: "submit" }, "Save password")),
    status,
  ));
  pass.focus();
}

async function boot() {
  const setup = /^#setup=([A-Za-z0-9_-]+)$/.exec(window.location.hash);
  if (setup) {
    history.replaceState(null, "", window.location.pathname);
    return showSetPassword(setup[1]);
  }
  try {
    const who = await api.session();
    if (who.username) signIn({ username: who.username, parent: who.parent });
    else showLogin();
  } catch {
    showLogin();
  }
}

window.addEventListener("hashchange", () => void boot());
void boot();
